import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { DeleteButton, List, SaveButton, useDataGrid } from "@refinedev/mui";
import type { HttpError } from "@refinedev/core";
import { useModalForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";

// A catalog row. Every row is a Story Studio entry, named by its hex shelf id
// (`id`, e.g. "0925d52c") — the same id every client sees; GET/PATCH/DELETE
// /admin/library/:id take it. The row mirrors the engine's shelf, so an edit
// here is made by the engine's own route and the row follows
// (server: services/admin-library.ts); only `ranking` is the row's own.
type LibraryRecord = {
  id: string;
  title: string;
  coverImage: string;
  coverThumb: string | null;
  tagLine: string | null;
  tags: string | null;
  ranking: number | null;
  visibility: "public" | "unlisted" | string;
  unpublishedAt: string | null;
  createdBy: number;
  authorName?: string | null;
  createdAt: string;
  // The list's v2 facts (from the entry): its rating, whether the catalog may
  // list it (rated sfw and checked at publish), the Studio's featured flag.
  contentRating?: string | null;
  ratingChecked?: boolean;
  featured?: boolean;
  // The detail read carries the entry whole.
  experience?: { featured?: boolean } | null;
};

type LibraryFormValues = {
  title: string;
  tagLine: string | null;
  coverImage: string;
  tags: string | null;
  visibility: "public" | "unlisted";
  featured: boolean;
  ranking: number | null;
};

// Why a row is or is not on the web's catalog (library.repository.ts onShelf).
const catalogStatus = (
  row: LibraryRecord
): { label: string; color: "success" | "default" | "warning"; hint: string } => {
  if (row.unpublishedAt)
    return { label: "Unpublished", color: "default", hint: "Taken off the shelf; its owner can put it back from Recently deleted." };
  if (row.contentRating !== "sfw")
    return { label: "Studio only", color: "warning", hint: "Rated mature: the web catalog lists only sfw stories. The Studio shows it to accounts allowed mature." };
  if (!row.ratingChecked)
    return { label: "Unchecked", color: "warning", hint: "Rated sfw but published before the rating check: publish it again (or run v2:rating-checked-backfill)." };
  return { label: "Listed", color: "success", hint: "On the web catalog." };
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const LibraryList = () => {
  const { dataGridProps } = useDataGrid({});

  const {
    modal: { visible, close, show },
    refineCore: { onFinish, formLoading, query },
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors },
    saveButtonProps,
  } = useModalForm<LibraryRecord, HttpError, LibraryFormValues>({
    refineCoreProps: { action: "edit", resource: "admin/library" },
  });

  const record = query?.data?.data;

  // The form's defaults are the row's columns; visibility and featured live in the entry.
  React.useEffect(() => {
    if (!record) return;
    reset({
      title: record.title,
      tagLine: record.tagLine,
      coverImage: record.coverImage,
      tags: record.tags,
      visibility: record.visibility === "unlisted" ? "unlisted" : "public",
      featured: record.experience?.featured === true,
      ranking: record.ranking,
    });
  }, [record, reset]);

  // Send only what the engine accepts (and the row's ranking); blank text
  // clears the tagline and genre, an empty ranking becomes null.
  const onSubmit = handleSubmit(async (values) => {
    const clean = (v?: string | null) => {
      const t = v?.toString().trim();
      return t ? t : null;
    };
    const ranking =
      values.ranking == null || Number.isNaN(values.ranking)
        ? null
        : Number(values.ranking);

    try {
      await onFinish({
        title: values.title,
        tagLine: clean(values.tagLine),
        coverImage: values.coverImage,
        tags: clean(values.tags),
        visibility: values.visibility,
        featured: Boolean(values.featured),
        ranking,
      });
      close();
    } catch {
      // Refine surfaces the server error via a notification; keep the modal open.
    }
  });

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "coverThumb",
        headerName: "Cover",
        minWidth: 72,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Avatar
            variant="rounded"
            src={row.coverThumb ?? row.coverImage ?? undefined}
            alt={row.title}
            sx={{ width: 40, height: 40 }}
          />
        ),
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 110,
        display: "flex",
        renderCell: ({ row }) => (
          <Typography variant="inherit" sx={{ fontFamily: "monospace" }}>
            {row.id}
          </Typography>
        ),
      },
      {
        field: "catalog",
        headerName: "Catalog",
        minWidth: 130,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          const status = catalogStatus(row);
          return (
            <Tooltip title={status.hint}>
              <Chip size="small" color={status.color} label={status.label} />
            </Tooltip>
          );
        },
      },
      {
        field: "contentRating",
        headerName: "Rating",
        minWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => row.contentRating ?? "-",
      },
      {
        field: "visibility",
        headerName: "Visibility",
        minWidth: 110,
        sortable: false,
        display: "flex",
      },
      {
        field: "featured",
        headerName: "Featured",
        minWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (row.featured ? <Chip size="small" color="primary" label="featured" /> : "-"),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.4,
        minWidth: 200,
        display: "flex",
      },
      {
        field: "createdBy",
        headerName: "Author",
        minWidth: 150,
        display: "flex",
        renderCell: ({ row }) =>
          row.authorName ? `${row.createdBy} (${row.authorName})` : row.createdBy,
      },
      {
        field: "ranking",
        headerName: "Ranking",
        minWidth: 110,
        display: "flex",
        renderCell: ({ row }) => (row.ranking == null ? "-" : row.ranking),
      },
      {
        field: "tagLine",
        headerName: "Tag Line",
        flex: 1.2,
        minWidth: 200,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Tooltip title={row.tagLine ?? ""}>
            <Typography variant="inherit" noWrap>
              {row.tagLine ?? "-"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "tags",
        headerName: "Genre",
        flex: 1,
        minWidth: 160,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          if (!row.tags) {
            return <span>-</span>;
          }
          const tags = String(row.tags)
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {tags.map((tag) => (
                <Chip key={tag} size="small" label={tag} />
              ))}
            </Stack>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Created",
        minWidth: 180,
        display: "flex",
        valueFormatter: (value) => formatDateTime(value as string | null | undefined),
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => show(row.id)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Unpublishes through the Studio: the entry goes to its owner's Recently deleted, the row stays. */}
            {row.unpublishedAt ? null : (
              <Tooltip title="Unpublish (to the owner's Recently deleted)">
                <span>
                  <DeleteButton hideText recordItemId={row.id} confirmTitle="Unpublish this story?" />
                </span>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [show]
  );

  return (
    <List canCreate={false}>
      <DataGrid {...dataGridProps} columns={columns} />

      <Dialog open={visible} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>
          Edit Library Entry
          {record ? ` · ${record.id}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {record ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              id {record.id} · owner {record.createdBy} · created{" "}
              {formatDateTime(record.createdAt)}
            </Typography>
          ) : null}

          <Box component="form" sx={{ display: "flex", flexDirection: "column" }} autoComplete="off">
            <TextField
              {...register("title", { required: "Title is required" })}
              error={!!errors?.title}
              helperText={errors?.title?.message?.toString()}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Title"
            />

            <TextField
              {...register("tagLine")}
              margin="normal"
              fullWidth
              multiline
              InputLabelProps={{ shrink: true }}
              label="Tag Line"
              helperText="The logline shown on the story tile (leave blank to clear)"
            />

            <TextField
              {...register("coverImage", {
                required: "Cover image is required",
                pattern: { value: /^https?:\/\//, message: "An http(s) address" },
              })}
              error={!!errors?.coverImage}
              helperText={errors?.coverImage?.message?.toString() ?? "The thumbnail is made again from it"}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Cover Image URL"
            />

            <TextField
              {...register("tags")}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Genre"
              helperText="One genre, e.g. fantasy (leave blank to clear)"
            />

            <Controller
              control={control}
              name="visibility"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? "public"}
                  select
                  margin="normal"
                  fullWidth
                  label="Visibility"
                  helperText="Unlisted: reachable by link, off every list"
                >
                  <MenuItem value="public">public</MenuItem>
                  <MenuItem value="unlisted">unlisted</MenuItem>
                </TextField>
              )}
            />

            <Controller
              control={control}
              name="featured"
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Featured"
                />
              )}
            />

            <TextField
              {...register("ranking", { valueAsNumber: true })}
              type="number"
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Ranking"
              helperText="Leave blank to clear"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <SaveButton {...saveButtonProps} onClick={onSubmit} disabled={formLoading} />
        </DialogActions>
      </Dialog>
    </List>
  );
};
